import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

const TikTok = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Footer = () => {
  return (
    <footer className="text-white" style={{ backgroundColor: '#0C4288' }}>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold text-[#50BAED] mb-2">Aquify</h3>
            <p className="text-xs font-semibold tracking-widest uppercase text-[#90D8F5] mb-4">
              Design&nbsp;•&nbsp;Sales&nbsp;•&nbsp;Installation&nbsp;•&nbsp;Service
            </p>
            <p className="text-gray-300 mb-5 leading-relaxed">
              Aquify provides professional water treatment design, sales, installation, and service for Long Island, NYC, Westchester, and South Florida.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/people/Aquify-Filtration-Partners/61583922105792/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Aquify on Facebook"
                className="bg-slate-800 p-2 rounded-full hover:bg-[#50BAED] transition-colors duration-300"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/aquify.filtration/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Aquify on Instagram"
                className="bg-slate-800 p-2 rounded-full hover:bg-[#50BAED] transition-colors duration-300"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@aquify.filtration"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Aquify on TikTok"
                className="bg-slate-800 p-2 rounded-full hover:bg-[#50BAED] transition-colors duration-300"
              >
                <TikTok className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-4">Our Services</h4>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/services#whole-house-systems" className="hover:text-[#50BAED] transition-colors duration-200">Whole House Systems</Link></li>
              <li><Link to="/services#reverse-osmosis" className="hover:text-[#50BAED] transition-colors duration-200">Reverse Osmosis Systems</Link></li>
              <li><Link to="/services#well-water-treatment" className="hover:text-[#50BAED] transition-colors duration-200">Well Water Treatment</Link></li>
              <li><Link to="/services#commercial-filtration" className="hover:text-[#50BAED] transition-colors duration-200">Commercial Filtration</Link></li>
              <li><Link to="/services#drinking-water-systems" className="hover:text-[#50BAED] transition-colors duration-200">Drinking Water Systems</Link></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/about" className="hover:text-[#50BAED] transition-colors duration-200">About Us</Link></li>
              <li><Link to="/services" className="hover:text-[#50BAED] transition-colors duration-200">Services</Link></li>
              <li><Link to="/water-guide" className="hover:text-[#50BAED] transition-colors duration-200">Water Guide</Link></li>
              <li><Link to="/newsletter" className="hover:text-[#50BAED] transition-colors duration-200">The Aquiflyer</Link></li>
              <li><Link to="/contact/" className="hover:text-[#50BAED] transition-colors duration-200">Contact Us</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4">Contact Us</h4>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#50BAED] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white">Phone</p>
                  <p>516-729-9013</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#50BAED] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white">Email</p>
                  <p>info@aquify.org</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#50BAED] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white">Service Areas</p>
                  <p>Long Island · NYC · Westchester</p>
                  <p>Palm Beach · Broward County</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Aquify Water Solutions. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="/privacy-policy/" className="hover:text-[#50BAED] transition-colors duration-200">Privacy Policy</a>
              <a href="/terms-of-service/" className="hover:text-[#50BAED] transition-colors duration-200">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
